import { useDispatch } from 'react-redux';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { reportIssueSuccessMessage } from 'dashboard/lib/constants/reportIssue';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { DocumentGeneric } from 'dashboard/models/document';
import { Verification } from 'dashboard/models/verification';
import ResourceVerification from 'dashboard/modules/ResourceVerification';
import { ModuleResourceVerificationV2 } from 'dashboard/modules/ResourceVerificationV2';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useGetVerificationObjectByIdQuery } from 'dashboard/redux/api/account';
import { useFlagVerificationIssuesMutation } from 'dashboard/redux/api/verification';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

export interface ResourceVerificationV2Props {
  module: ModuleResourceVerificationV2;
  currentObjectToken: string;
  verificationToken: string;
  featureFlags: FeatureFlagsMap;
}

const ResourceVerificationV2 = ({
  currentObjectToken,
  verificationToken,
  module,
  featureFlags,
}: ResourceVerificationV2Props) => {
  const dispatch = useDispatch();

  const { data, error, isLoading } = useGetVerificationObjectByIdQuery({
    id: currentObjectToken,
    verificationId: verificationToken,
  });

  useRTKErrorHandler({
    error,
    onError: () => {
      return {
        message: 'Failed to load Verification.',
        level: MessageType.Failure,
      };
    },
  });

  const [onFlagVerificationIssues, { error: flagVerificationIssuesError }] =
    useFlagVerificationIssuesMutation();

  useRTKErrorHandler({
    error: flagVerificationIssuesError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  const handleFlagVerificationIssues = async (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => {
    await onFlagVerificationIssues({ id: verification.id, ...values });

    dispatch(showMessage(reportIssueSuccessMessage(values.responseType), MessageType.Success));
  };

  if (isLoading || !data) {
    return <LoadingIndicator />;
  }

  const verification = data.data;
  const documents = data.included?.filter(
    (item) => item.type === ApiModel.DocumentGeneric,
  ) as DocumentGeneric[];

  // Filter on verification type and verification template
  if (
    (module['verification-type'] && verification.type !== module['verification-type']) ||
    (module['verification-templates'].length > 0 &&
      verification.relationships['verification-template']?.data?.id &&
      !module['verification-templates'].includes(
        verification.relationships['verification-template']?.data?.id,
      ))
  ) {
    return null;
  }

  return (
    <ResourceVerification
      verification={verification}
      documents={documents}
      defaultAccordionOpen={module['default-accordion-open'] ?? true}
      featureFlags={featureFlags}
      handleFlagVerificationIssues={handleFlagVerificationIssues}
    />
  );
};

export default ResourceVerificationV2;
